import React from 'react'
import { cn } from '../../utils/cn'

/**
 * Surface — the base panel. Quiet by default; `tone="raised"` lifts it.
 */
export function Surface({ as: As = 'div', tone = 'base', padded = false, className, children, ...rest }) {
  const tones = {
    base:   'bg-white border border-ink-100 shadow-elev-1',
    raised: 'bg-white border border-ink-100 shadow-elev-2',
    quiet:  'bg-ink-50/60 border border-ink-100',
    ink:    'bg-ink-900 border border-ink-800 text-white',
  }
  return (
    <As className={cn('rounded-2xl', tones[tone] || tones.base, padded && 'p-5', className)} {...rest}>
      {children}
    </As>
  )
}

/**
 * PanelHeader — kicker + title on the left, action slot on the right.
 */
export function PanelHeader({ kicker, title, sub, action, className }) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        {kicker && <div className="kicker mb-1">{kicker}</div>}
        {title && <h3 className="text-base font-semibold text-ink-900 leading-tight">{title}</h3>}
        {sub && <p className="text-sm text-ink-500 mt-0.5">{sub}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

/**
 * Panel — Surface + standard padding + optional header. The everyday container.
 */
export function Panel({ kicker, title, sub, action, header, className, bodyClassName, children, tone, ...rest }) {
  const hasHeader = header || title || kicker
  return (
    <Surface tone={tone} className={cn('p-5 sm:p-6', className)} {...rest}>
      {hasHeader && (
        <div className="mb-4">
          {header || <PanelHeader kicker={kicker} title={title} sub={sub} action={action} />}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </Surface>
  )
}

export default Surface
